/**
 * One WebSocket to the price service (`/v1/stream`) shared by the whole app.
 *
 * - Subscriptions are reference counted per price market and per candle
 *   (market, interval). The last listener leaving starts a short idle timer
 *   before the server-side unsubscribe, so quick navigation reuses them.
 * - New price subscriptions get a snapshot from `/v1/prices` (batched), since
 *   the stream only pushes when a price changes.
 * - Reconnects with backoff, opens a fresh socket with a renewed token before
 *   the current one expires, and resubscribes everything on every new socket.
 */

import { marketKey, type ServiceExchange } from "./exchanges";
import type { ServiceInterval } from "./intervals";
import {
  toPriceUpdate,
  toServiceCandle,
  type PriceUpdate,
  type ServiceCandle,
} from "./price-service.types";
import { MIN_RENEWAL_DELAY_MS, type PriceServiceToken } from "./price-token";

/** Minimal WebSocket surface the stream needs (the browser one fits). */
export interface StreamSocket {
  readonly readyState: number;
  send(data: string): void;
  close(): void;
  onopen: ((event: Event) => void) | null;
  onmessage: ((event: MessageEvent) => void) | null;
  onclose: ((event: CloseEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
}

export interface PriceStreamTokens {
  get(): Promise<PriceServiceToken>;
  invalidate(rejected?: string): void;
  msUntilRenewal(): number;
}

export interface PriceStreamOptions {
  tokens: PriceStreamTokens;
  createSocket: (url: string) => StreamSocket;
  /** Snapshot for newly subscribed markets. */
  fetchPrices?: (
    markets: Array<{ exchange: ServiceExchange; symbol: string }>
  ) => Promise<PriceUpdate[]>;
  idleUnsubscribeMs?: number;
}

export interface PriceListener {
  onPrice: (update: PriceUpdate) => void;
  /** `listed: false` when the service does not price this market. */
  onListed?: (listed: boolean) => void;
}

export interface CandleListener {
  onCandle: (candle: ServiceCandle) => void;
}

const SOCKET_OPEN = 1;
const DEFAULT_IDLE_UNSUBSCRIBE_MS = 30_000;
const SNAPSHOT_BATCH_MS = 50;
const HEARTBEAT_MS = 20_000;
const STALE_AFTER_MS = 50_000;
const MAX_BACKOFF_MS = 30_000;
/** Hydrate a re-subscribed market from its last price if it is this recent. */
const LAST_PRICE_TTL_MS = 120_000;

interface PriceEntry {
  exchange: ServiceExchange;
  symbol: string;
  listeners: Set<PriceListener>;
  idleTimer: ReturnType<typeof setTimeout> | null;
}

interface CandleEntry {
  exchange: ServiceExchange;
  symbol: string;
  interval: ServiceInterval;
  listeners: Set<CandleListener>;
  idleTimer: ReturnType<typeof setTimeout> | null;
}

export function candleKey(
  exchange: ServiceExchange,
  symbol: string,
  interval: ServiceInterval
): string {
  return `${exchange}:${symbol}:${interval}`;
}

export function toStreamUrl(baseUrl: string, token: string): string {
  const wsBase = baseUrl.replace(/^http/i, "ws").replace(/\/+$/, "");
  return `${wsBase}/v1/stream?token=${encodeURIComponent(token)}`;
}

export class PriceStream {
  private readonly prices = new Map<string, PriceEntry>();
  private readonly candles = new Map<string, CandleEntry>();
  private readonly lastPrices = new Map<
    string,
    { update: PriceUpdate; receivedAt: number }
  >();
  private readonly pendingSnapshots = new Set<string>();
  private snapshotTimer: ReturnType<typeof setTimeout> | null = null;

  private socket: StreamSocket | null = null;
  private socketToken: string | null = null;
  /** A socket that is still opening (first connect, reconnect or rotation). */
  private opening: StreamSocket | null = null;
  private connecting = false;
  private attempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private rotateTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private lastMessageAt = 0;
  private readonly idleUnsubscribeMs: number;

  constructor(private readonly options: PriceStreamOptions) {
    this.idleUnsubscribeMs =
      options.idleUnsubscribeMs ?? DEFAULT_IDLE_UNSUBSCRIBE_MS;
  }

  /**
   * Last known price for a market: always while it is subscribed (the stream
   * only pushes changes), otherwise only if it arrived recently.
   */
  getLastPrice(exchange: ServiceExchange, symbol: string): PriceUpdate | null {
    const key = marketKey(exchange, symbol);
    const last = this.lastPrices.get(key);
    if (!last) return null;
    if (this.prices.has(key)) return last.update;
    if (Date.now() - last.receivedAt > LAST_PRICE_TTL_MS) {
      this.lastPrices.delete(key);
      return null;
    }
    return last.update;
  }

  subscribePrice(
    exchange: ServiceExchange,
    symbol: string,
    listener: PriceListener
  ): () => void {
    const key = marketKey(exchange, symbol);
    const hydrated = this.getLastPrice(exchange, symbol);
    let entry = this.prices.get(key);
    if (!entry) {
      entry = { exchange, symbol, listeners: new Set(), idleTimer: null };
      this.prices.set(key, entry);
      this.sendIfOpen({
        op: "subscribe",
        channel: "prices",
        markets: [{ exchange, symbol }],
      });
      this.queueSnapshot(key);
    }
    if (entry.idleTimer) {
      clearTimeout(entry.idleTimer);
      entry.idleTimer = null;
    }
    entry.listeners.add(listener);
    this.ensureConnected();

    if (hydrated) listener.onPrice(hydrated);

    let active = true;
    return () => {
      if (!active) return;
      active = false;
      const current = this.prices.get(key);
      if (!current) return;
      current.listeners.delete(listener);
      if (current.listeners.size > 0) return;
      current.idleTimer = setTimeout(() => {
        const again = this.prices.get(key);
        if (!again || again.listeners.size > 0) return;
        this.prices.delete(key);
        this.pendingSnapshots.delete(key);
        this.sendIfOpen({
          op: "unsubscribe",
          channel: "prices",
          markets: [{ exchange, symbol }],
        });
      }, this.idleUnsubscribeMs);
    };
  }

  subscribeCandles(
    exchange: ServiceExchange,
    symbol: string,
    interval: ServiceInterval,
    listener: CandleListener
  ): () => void {
    const key = candleKey(exchange, symbol, interval);
    let entry = this.candles.get(key);
    if (!entry) {
      entry = {
        exchange,
        symbol,
        interval,
        listeners: new Set(),
        idleTimer: null,
      };
      this.candles.set(key, entry);
      this.sendIfOpen({
        op: "subscribe",
        channel: "candles",
        exchange,
        symbol,
        interval,
      });
    }
    if (entry.idleTimer) {
      clearTimeout(entry.idleTimer);
      entry.idleTimer = null;
    }
    entry.listeners.add(listener);
    this.ensureConnected();

    let active = true;
    return () => {
      if (!active) return;
      active = false;
      const current = this.candles.get(key);
      if (!current) return;
      current.listeners.delete(listener);
      if (current.listeners.size > 0) return;
      current.idleTimer = setTimeout(() => {
        const again = this.candles.get(key);
        if (!again || again.listeners.size > 0) return;
        this.candles.delete(key);
        this.sendIfOpen({
          op: "unsubscribe",
          channel: "candles",
          exchange,
          symbol,
          interval,
        });
      }, this.idleUnsubscribeMs);
    };
  }

  /** Reconnect right away (e.g. when the app comes back to the foreground). */
  reconnectNow(): void {
    if (this.socket || this.connecting || !this.hasSubscriptions()) return;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.attempts = 0;
    this.connect();
  }

  /** Handles one raw stream message. Public for tests. */
  handleMessage(raw: unknown): void {
    this.lastMessageAt = Date.now();
    let message: Record<string, unknown>;
    try {
      message =
        typeof raw === "string"
          ? (JSON.parse(raw) as Record<string, unknown>)
          : (raw as Record<string, unknown>);
    } catch {
      return;
    }
    if (!message || typeof message !== "object") return;

    switch (message.type) {
      case "price": {
        const update = toPriceUpdate(message);
        if (update) this.emitPrice(update);
        return;
      }
      case "candle": {
        if (
          typeof message.exchange !== "string" ||
          typeof message.symbol !== "string" ||
          typeof message.interval !== "string"
        ) {
          return;
        }
        const entry = this.candles.get(
          `${message.exchange}:${message.symbol}:${message.interval}`
        );
        const candle = toServiceCandle(message.candle);
        if (!entry || !candle) return;
        entry.listeners.forEach((listener) => listener.onCandle(candle));
        return;
      }
      case "subscribed": {
        if (
          message.channel !== "prices" ||
          typeof message.exchange !== "string" ||
          typeof message.symbol !== "string" ||
          typeof message.listed !== "boolean"
        ) {
          return;
        }
        const entry = this.prices.get(`${message.exchange}:${message.symbol}`);
        const listed = message.listed;
        entry?.listeners.forEach((listener) => listener.onListed?.(listed));
        return;
      }
      case "expired": {
        this.options.tokens.invalidate(this.socketToken ?? undefined);
        this.attempts = 0;
        return;
      }
      case "error": {
        console.warn("[PriceStream] error from price service:", message.message);
        return;
      }
      default:
        return;
    }
  }

  private emitPrice(update: PriceUpdate): void {
    const key = marketKey(update.exchange, update.symbol);
    const previous = this.lastPrices.get(key);
    if (previous && previous.update.timestamp > update.timestamp) return;
    this.lastPrices.set(key, { update, receivedAt: Date.now() });
    const entry = this.prices.get(key);
    entry?.listeners.forEach((listener) => listener.onPrice(update));
  }

  private hasSubscriptions(): boolean {
    return this.prices.size > 0 || this.candles.size > 0;
  }

  private queueSnapshot(key: string): void {
    if (!this.options.fetchPrices) return;
    this.pendingSnapshots.add(key);
    if (this.snapshotTimer) return;
    this.snapshotTimer = setTimeout(() => {
      this.snapshotTimer = null;
      const markets = Array.from(this.pendingSnapshots)
        .map((pending) => this.prices.get(pending))
        .filter((entry): entry is PriceEntry => Boolean(entry))
        .map((entry) => ({ exchange: entry.exchange, symbol: entry.symbol }));
      this.pendingSnapshots.clear();
      if (markets.length === 0 || !this.options.fetchPrices) return;
      this.options
        .fetchPrices(markets)
        .then((updates) => updates.forEach((update) => this.emitPrice(update)))
        .catch((error: unknown) => {
          console.warn("[PriceStream] price snapshot failed:", error);
        });
    }, SNAPSHOT_BATCH_MS);
  }

  private sendIfOpen(payload: Record<string, unknown>): void {
    const socket = this.socket;
    if (!socket || socket.readyState !== SOCKET_OPEN) return;
    try {
      socket.send(JSON.stringify(payload));
    } catch {
      /* the close handler reconnects */
    }
  }

  private sendAllSubscriptions(socket: StreamSocket): void {
    const markets = Array.from(this.prices.values()).map((entry) => ({
      exchange: entry.exchange,
      symbol: entry.symbol,
    }));
    const send = (payload: Record<string, unknown>) => {
      try {
        socket.send(JSON.stringify(payload));
      } catch {
        /* the close handler reconnects */
      }
    };
    if (markets.length > 0) {
      send({ op: "subscribe", channel: "prices", markets });
    }
    this.candles.forEach((entry) =>
      send({
        op: "subscribe",
        channel: "candles",
        exchange: entry.exchange,
        symbol: entry.symbol,
        interval: entry.interval,
      })
    );
  }

  private ensureConnected(): void {
    if (this.socket || this.connecting || this.reconnectTimer) return;
    this.connect();
  }

  private connect(): void {
    if (this.connecting) return;
    this.connecting = true;
    this.options.tokens
      .get()
      .then((token) => this.openSocket(token))
      .catch((error: unknown) => {
        this.connecting = false;
        console.warn("[PriceStream] could not get a price token:", error);
        this.scheduleReconnect();
      });
  }

  private openSocket(token: PriceServiceToken): void {
    let socket: StreamSocket;
    try {
      socket = this.options.createSocket(toStreamUrl(token.url, token.token));
    } catch (error) {
      this.connecting = false;
      console.warn("[PriceStream] could not open the price stream:", error);
      this.scheduleReconnect();
      return;
    }
    this.opening = socket;

    socket.onopen = () => {
      if (this.opening !== socket) return;
      this.opening = null;
      this.connecting = false;
      this.attempts = 0;
      const previous = this.socket;
      this.socket = socket;
      this.socketToken = token.token;
      this.lastMessageAt = Date.now();
      this.sendAllSubscriptions(socket);
      if (previous && previous !== socket) this.discard(previous);
      this.startHeartbeat();
      this.scheduleRotation();
    };
    socket.onmessage = (event) => {
      if (this.socket !== socket) return;
      this.handleMessage(event.data);
    };
    socket.onerror = () => {
      /* onclose follows */
    };
    socket.onclose = () => {
      if (this.opening === socket) {
        this.opening = null;
        this.connecting = false;
        if (!this.socket) this.scheduleReconnect();
        return;
      }
      if (this.socket !== socket) return;
      this.socket = null;
      this.socketToken = null;
      this.stopTimers();
      if (this.hasSubscriptions()) this.scheduleReconnect();
    };
  }

  private discard(socket: StreamSocket): void {
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;
    try {
      socket.close();
    } catch {
      /* ignore */
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || !this.hasSubscriptions()) return;
    const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** this.attempts);
    this.attempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.socket || !this.hasSubscriptions()) return;
      this.connect();
    }, delay);
  }

  /**
   * Swap to a socket with a fresh token about a minute before expiry; never
   * sooner than MIN_RENEWAL_DELAY_MS, so a bad expiry cannot loop.
   */
  private scheduleRotation(): void {
    if (this.rotateTimer) clearTimeout(this.rotateTimer);
    this.rotateTimer = setTimeout(() => {
      this.rotateTimer = null;
      const current = this.socket;
      if (!current) return;
      if (!this.hasSubscriptions()) {
        this.socket = null;
        this.socketToken = null;
        this.stopTimers();
        this.discard(current);
        return;
      }
      this.connect();
    }, Math.max(MIN_RENEWAL_DELAY_MS, this.options.tokens.msUntilRenewal()));
  }

  private startHeartbeat(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = setInterval(() => {
      const socket = this.socket;
      if (!socket) return;
      if (Date.now() - this.lastMessageAt > STALE_AFTER_MS) {
        this.socket = null;
        this.socketToken = null;
        this.stopTimers();
        this.discard(socket);
        this.scheduleReconnect();
        return;
      }
      this.sendIfOpen({ op: "ping" });
    }, HEARTBEAT_MS);
  }

  private stopTimers(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.rotateTimer) {
      clearTimeout(this.rotateTimer);
      this.rotateTimer = null;
    }
  }
}
